import componentService from "core/services/componentService/component-service";
import utils from "core/utils";

// ============================================================================
// COMPONENT IDENTIFICATION UTILITIES
// ============================================================================

/**
 * Gets the component ID based on component type and properties
 */
export const getComponentId = (component: any, type: string) => {
    switch (type) {
        case utils.ENTITIES.SOURCE:
        case utils.ENTITIES.TARGET:
            const adapterName = component?.adapter?.properties?.adapterName;
            return adapterName === "FileReader"
                ? componentService.getComponentIDFromParserName(component?.parser?.properties?.parserName)
                : componentService.getComponentIDFromAdapterName(adapterName);
        case utils.ENTITIES.CACHE:
            const parserName = utils.getName(component?.adapter?.handler);
            return componentService.getComponentIDFromName(parserName);
        default:
            return componentService.getComponentIDFromName(component.type);
    }
};

// ============================================================================
// COMPONENT CONNECTION UTILITIES
// ============================================================================

/**
 * Used to find Components in OTHER flows that reference components in the current flow
 */
export const findReferencedComponents = (component: any): string[] => {
    const referencedIds: string[] = [];
    const componentType = component.type;

    // Check different types of connections based on component type
    const addReference = (targetId: string) => {
        if (targetId) {
            referencedIds.push(targetId);
        }
    };

    // Handle different component types and their connection properties
    if (
        (componentType === utils.ENTITIES.SOURCE || componentType === utils.ENTITIES.EXTERNALSOURCE) &&
        component.outputStream
    ) {
        addReference(component.outputStream);
    }

    if (componentType === utils.ENTITIES.TARGET && component.inputStream) {
        addReference(component.inputStream);
    }

    if (componentType === utils.ENTITIES.CQ) {
        if (component.inputs && Array.isArray(component.inputs)) {
            component.inputs.forEach((input: string) => addReference(input));
        }
        if (component.output) {
            addReference(component.output);
        }
    }

    if (componentType === utils.ENTITIES.WINDOW) {
        if (component.stream) {
            addReference(component.stream);
        }
    }

    if (componentType === utils.ENTITIES.ROUTER) {
        if (component.inStream) {
            addReference(component.inStream);
        }
        if (component.forwardingRules && Array.isArray(component.forwardingRules)) {
            component.forwardingRules.forEach((rule: any) => {
                if (rule.outStream) {
                    addReference(rule.outStream);
                }
            });
        }
    }

    if (componentType === utils.ENTITIES.OPENPROCESSOR) {
        if (component.from) {
            const inputs = Array.isArray(component.from) ? component.from : [component.from];
            inputs.forEach((inputId: string) => addReference(inputId));
        }
        if (component.output) {
            addReference(component.output);
        }
    }

    if (componentType === utils.ENTITIES.SENTINEL) {
        if (component.inputStream) {
            addReference(component.inputStream);
        }
        if (component.outputStream) {
            addReference(component.outputStream);
        }
    }

    return referencedIds;
};

// ============================================================================
// COMPONENT TYPE UTILITIES
// ============================================================================

/**
 * Determines the node type based on component type
 */
export const getNodeType = (componentType: string): string => {
    if (componentType === utils.ENTITIES.SOURCE || componentType === utils.ENTITIES.EXTERNALSOURCE) {
        return utils.ENTITIES.SOURCE;
    } else if (componentType === utils.ENTITIES.TARGET || componentType === utils.ENTITIES.WACTIONSTORE) {
        return utils.ENTITIES.TARGET;
    } else if (componentType === utils.ENTITIES.FLOW) {
        return utils.ENTITIES.FLOW;
    }
    return componentType;
};

/**
 * Checks if a component type should be excluded from flow visualization
 */
export const shouldExcludeComponentType = (componentType: string): boolean => {
    return (
        !componentType ||
        componentType === utils.ENTITIES.TYPE ||
        componentType === "PROPERTYSET" ||
        componentType === "DASHBOARD"
    );
};
